export class HistogramPanel {
  private container: HTMLElement;
  private canvas: HTMLCanvasElement;
  private statsEl: HTMLElement;
  private channelSelect: HTMLSelectElement;
  private currentChannel: "rgb" | "r" | "g" | "b" = "rgb";

  constructor() {
    this.container = document.createElement("div");
    this.container.className = "histogram-panel";
    this.container.innerHTML = `
      <div class="panel-title">Histogram</div>
      <div class="histogram-channel-select">
        <select class="histogram-select">
          <option value="rgb">RGB</option>
          <option value="r">Red</option>
          <option value="g">Green</option>
          <option value="b">Blue</option>
        </select>
      </div>
      <canvas class="histogram-canvas" width="256" height="100"></canvas>
      <div class="histogram-stats"></div>
    `;
    this.canvas = this.container.querySelector(".histogram-canvas")!;
    this.statsEl = this.container.querySelector(".histogram-stats")!;
    this.channelSelect = this.container.querySelector(".histogram-select")!;
    this.channelSelect.addEventListener("change", () => {
      this.currentChannel = this.channelSelect.value as "rgb" | "r" | "g" | "b";
      this.render();
    });
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.container);
  }

  update(imageData: ImageData | null, selectionMask?: Uint8Array | null): void {
    if (!imageData) {
      this.statsEl.innerHTML = '<div class="muted" style="padding:8px;text-align:center">No document</div>';
      return;
    }
    this._imageData = imageData;
    this._selectionMask = selectionMask ?? null;
    this.render();
  }

  private _imageData: ImageData | null = null;
  private _selectionMask: Uint8Array | null = null;

  private render(): void {
    if (!this._imageData) return;
    const data = this._imageData.data;
    const w = this._imageData.width;
    const h = this._imageData.height;
    const mask = this._selectionMask;

    const histR = new Uint32Array(256);
    const histG = new Uint32Array(256);
    const histB = new Uint32Array(256);
    let pixelCount = 0;
    let sumR = 0, sumG = 0, sumB = 0;

    for (let i = 0; i < w * h; i++) {
      if (mask && mask[i] === 0) continue;
      const idx = i * 4;
      const r = data[idx]!, g = data[idx + 1]!, b = data[idx + 2]!;
      histR[r] = (histR[r] || 0) + 1;
      histG[g] = (histG[g] || 0) + 1;
      histB[b] = (histB[b] || 0) + 1;
      sumR += r;
      sumG += g;
      sumB += b;
      pixelCount++;
    }

    if (pixelCount === 0) {
      this.statsEl.innerHTML = '<div class="muted" style="padding:8px;text-align:center">No pixels</div>';
      return;
    }

    const meanR = sumR / pixelCount;
    const meanG = sumG / pixelCount;
    const meanB = sumB / pixelCount;

    let varR = 0, varG = 0, varB = 0;
    let medianR = 0, medianG = 0, medianB = 0;
    let cumR = 0, cumG = 0, cumB = 0;
    const half = pixelCount / 2;
    for (let i = 0; i < 256; i++) {
      varR += histR[i]! * (i - meanR) ** 2;
      varG += histG[i]! * (i - meanG) ** 2;
      varB += histB[i]! * (i - meanB) ** 2;
      if (cumR < half && (cumR + histR[i]!) >= half) medianR = i;
      if (cumG < half && (cumG + histG[i]!) >= half) medianG = i;
      if (cumB < half && (cumB + histB[i]!) >= half) medianB = i;
      cumR += histR[i]!;
      cumG += histG[i]!;
      cumB += histB[i]!;
    }
    const stdR = Math.sqrt(varR / pixelCount);
    const stdG = Math.sqrt(varG / pixelCount);
    const stdB = Math.sqrt(varB / pixelCount);

    // Draw histogram
    const ctx = this.canvas.getContext("2d")!;
    const cw = this.canvas.width;
    const ch = this.canvas.height;
    ctx.fillStyle = "#1a1a1a";
    ctx.fillRect(0, 0, cw, ch);

    let maxVal = 0;
    const hist = this.currentChannel === "rgb"
      ? histR.map((v, i) => v + histG[i]! + histB[i]!)
      : this.currentChannel === "r" ? histR
        : this.currentChannel === "g" ? histG : histB;
    for (let i = 0; i < 256; i++) {
      if (hist[i]! > maxVal) maxVal = hist[i]!;
    }

    if (maxVal === 0) return;

    const barW = cw / 256;
    if (this.currentChannel === "rgb") {
      this._drawChannel(ctx, histR, maxVal, barW, ch, "rgba(255,0,0,0.4)");
      this._drawChannel(ctx, histG, maxVal, barW, ch, "rgba(0,255,0,0.4)");
      this._drawChannel(ctx, histB, maxVal, barW, ch, "rgba(0,0,255,0.4)");
    } else {
      const color = this.currentChannel === "r" ? "rgba(255,80,80,0.7)" :
        this.currentChannel === "g" ? "rgba(80,255,80,0.7)" : "rgba(80,80,255,0.7)";
      this._drawChannel(ctx, hist, maxVal, barW, ch, color);
    }

    // Stats
    const mean = this.currentChannel === "rgb" ? (meanR + meanG + meanB) / 3 :
      this.currentChannel === "r" ? meanR : this.currentChannel === "g" ? meanG : meanB;
    const std = this.currentChannel === "rgb" ? (stdR + stdG + stdB) / 3 :
      this.currentChannel === "r" ? stdR : this.currentChannel === "g" ? stdG : stdB;
    const median = this.currentChannel === "rgb" ? Math.round((medianR + medianG + medianB) / 3) :
      this.currentChannel === "r" ? medianR : this.currentChannel === "g" ? medianG : medianB;
    const levels = hist.filter(v => v > 0).length;

    this.statsEl.innerHTML = `
      <div class="histogram-stats__row"><span class="muted">Pixels:</span> <span>${pixelCount.toLocaleString()}</span></div>
      <div class="histogram-stats__row"><span class="muted">Mean:</span> <span>${mean.toFixed(1)}</span></div>
      <div class="histogram-stats__row"><span class="muted">Median:</span> <span>${median}</span></div>
      <div class="histogram-stats__row"><span class="muted">StdDev:</span> <span>${std.toFixed(1)}</span></div>
      <div class="histogram-stats__row"><span class="muted">Levels:</span> <span>${levels}</span></div>
    `;
  }

  private _drawChannel(
    ctx: CanvasRenderingContext2D,
    hist: Uint32Array,
    maxVal: number,
    barW: number,
    ch: number,
    color: string,
  ): void {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(0, ch);
    for (let i = 0; i < 256; i++) {
      const barH = (hist[i]! / maxVal) * ch;
      ctx.lineTo(i * barW, ch - barH);
    }
    ctx.lineTo(255 * barW, ch);
    ctx.closePath();
    ctx.fill();
  }

  destroy(): void {
    this.container.remove();
  }
}
